const EMAIL = "dr.sangramj@gmail.com";

export const POLICY_SECTIONS = [
  {
    id: "who-we-are", short: "Who we are", title: "Who we are",
    paragraphs: [
      "LocumConnect connects hospitals and clinics with verified locum doctors and nurses in India. This policy explains what personal data we collect, why, who sees it, and how you can control it. It applies to our website (locumorg.com), our WhatsApp service and related services.",
      "LocumConnect is operated by Dr. Sangram Jadhav, Pune, Maharashtra, India (\"we\", \"us\"). Under India's Digital Personal Data Protection Act, 2023, we are the Data Fiduciary for the personal data described here.",
      `Contact and Grievance Officer: Dr. Sangram Jadhav, email ${EMAIL}, Pune, Maharashtra, India. We reply within 30 days.`,
    ],
  },
  {
    id: "what-we-collect", short: "What we collect", title: "What we collect",
    table: {
      head: ["Who", "What", "Source"],
      rows: [
        ["Doctors and nurses", "Name, phone number, email, city, specialty, availability", "From you, on the website or WhatsApp"],
        ["Doctors and nurses", "Council registration certificate, degree certificate, government ID (Aadhaar, PAN or passport)", "Photos or PDFs you upload"],
        ["Doctors and nurses", "Bank or UPI details for payouts", "From you"],
        ["Doctors and nurses", "Shifts applied for and worked, hours, ratings", "From your use of the service and the hospital"],
        ["Hospitals and clinics", "Facility name and address, contact person's name, phone and email, shift details", "From you"],
        ["Anyone using WhatsApp", "WhatsApp number, profile name, messages and button replies sent to our number", "From WhatsApp/Meta"],
        ["Website visitors", "Google account name and email if you sign in with Google; basic technical data such as IP address, device and browser type", "From your browser and Google"],
      ],
    },
    after: [
      "Aadhaar: we never ask you to type your Aadhaar number. If you upload an Aadhaar card, please mask the first 8 digits. We use it only to confirm your identity, not for Aadhaar authentication.",
      "We do not collect patient information; please do not send patient details to us.",
    ],
  },
  {
    id: "how-we-use-it", short: "How we use it", title: "How we use it",
    paragraphs: ["Only with your consent, given when you sign up or message us, and only to:"],
    list: [
      "check your registration, qualifications and identity before you can apply for shifts;",
      "show you shifts that match your specialty, city and availability;",
      "send shift alerts, confirmations, reminders and payment updates on WhatsApp;",
      "pay you for completed shifts and keep records required by tax law;",
      "handle disputes, safety concerns and support;",
      "keep the service secure and prevent fraud.",
    ],
    after: ["We do not sell your personal data and do not use it for advertising."],
  },
  {
    id: "who-we-share-with", short: "Sharing", title: "Who we share it with",
    table: {
      head: ["Recipient", "What they see", "Why"],
      rows: [
        ["Hospitals and clinics you apply to", "Your name, specialty, verified badge, ratings and contact number, not your ID documents", "So they can choose and contact you"],
        ["Doctors and nurses who apply to your shift", "Facility name, address, shift details and contact person", "So they can work the shift"],
        ["Our hosting provider (Emergent)", "All data, stored on our behalf", "To run the website and store files securely"],
        ["Meta (WhatsApp)", "Your WhatsApp number and messages with us", "To deliver WhatsApp messages; Meta's own privacy policy also applies"],
        ["Google", "Your sign-in details if you use Google login", "To sign you in"],
        ["Our payment provider", "Bank or UPI details and payout amounts", "To send your payments"],
        ["Government or courts", "Only what the law requires", "Legal obligations"],
      ],
    },
    after: [
      "Our service providers may store or process data outside India; we only use providers that protect it to standards at least as strong as this policy, and we follow any restrictions the Government of India places on transfers abroad.",
    ],
  },
  {
    id: "security", short: "Security", title: "Security",
    paragraphs: [
      "Data is encrypted in transit. Uploaded documents are kept in private cloud storage and can be opened only by you and our authorised verification staff. Messages to our WhatsApp webhook are checked to confirm they come from Meta.",
      "If a personal data breach affects you, we will tell you without delay and report it to the Data Protection Board of India as the law requires.",
    ],
  },
  {
    id: "how-long", short: "Retention", title: "How long we keep it",
    table: {
      head: ["Data", "How long"],
      rows: [
        ["Profile and verification documents", "While your account is active, then deleted within 90 days of closing it"],
        ["Shift and payment records", "8 years, as required by Indian tax and accounting law"],
        ["WhatsApp messages with us", "12 months, unless needed for an open dispute"],
        ["Security and access logs", "At least 1 year"],
      ],
    },
    after: ["Before deleting your data because you stopped using the service, we will notify you at least 48 hours in advance."],
  },
  {
    id: "your-rights", short: "Your rights", title: "Your rights",
    paragraphs: ["Under the DPDP Act you can:"],
    list: [
      "see a summary of the data we hold and who we shared it with;",
      "correct inaccurate data;",
      "ask us to erase your data, except records the law requires us to keep;",
      "withdraw consent at any time (on WhatsApp reply STOP to stop messages; to close your account email us);",
      "nominate someone to exercise these rights if you die or cannot act;",
      "complain to our Grievance Officer, and if not satisfied, to the Data Protection Board of India.",
    ],
    after: [`To use these rights, email ${EMAIL} from the email or phone number linked to your account.`],
  },
  {
    id: "age", short: "Age", title: "Age",
    paragraphs: ["LocumConnect is for qualified professionals and facilities only; we do not knowingly collect data from anyone under 18."],
  },
  {
    id: "cookies", short: "Cookies", title: "Cookies",
    paragraphs: [
      "We use a sign-in cookie to keep you signed in, and small items in your browser's local storage to remember your choices (such as whether you are a professional or a facility) and to give each visit a random session ID.",
      "We also measure how the site is used so we can improve it: we record which pages are visited and which buttons are used, linked to your account if you are signed in. Our hosting provider (Emergent) also provides analytics on our behalf, which sets its own cookies and records page visits and clicks; it does not record your screen or what you type. Our payment provider (Razorpay) loads its checkout on our site and may set its own cookies to process payments securely.",
      "We do not use advertising cookies and we do not use this information for advertising.",
    ],
  },
  {
    id: "changes", short: "Changes", title: "Changes",
    paragraphs: ["If we change this policy in a way that affects you, we will tell you on the website or by WhatsApp before it takes effect."],
  },
  {
    id: "deleting-your-data", short: "Delete your data", title: "Deleting your data",
    paragraphs: [`Email ${EMAIL} with the subject "Delete my data", or reply DELETE MY DATA to our WhatsApp number. We will confirm within 30 days.`],
  },
];
